import { t, Trans } from "@lingui/macro";
import { Box, Stack, Switch, Typography } from "@mui/material";
import { BoltDisplayField, crux_defn_type_e } from "@WahooFitness/crux-js-display-cfg";
import {
  FancyIcon,
  MenuList,
  MenuListItemVariant,
  WfIconMiscChartBarRegular,
  WfIconMiscMapTrifoldRegular,
  WfIconNavigationChevronRightRegular,
  WfIconSportBoltRegular,
  WfIconSportMountainsRegular,
  WfIconSportWindRegular,
} from "@WahooFitness/redesignr";
import { useMemo } from "react";
import useWorkoutPageFieldStrings from "../useWorkoutPageFieldStrings";
import { WSMWorkoutProfile } from "@WahooFitness/wsm-native/dist/esm/types/workout_profile";
import { WahooWorkoutTypeFamily } from "@WahooFitness/cloud-client-types";

const cruxPermanentFieldsIconMap = {
  [crux_defn_type_e.SEGMENT_DURATION_ACTIVE]: <WfIconSportMountainsRegular />,
  [crux_defn_type_e.CLIMB_DURATION_ACTIVE]: <WfIconSportMountainsRegular />,
  [crux_defn_type_e.WIND_DIAL]: <WfIconSportWindRegular />,
};

const getCruxPermanentFieldDescription = (cruxDefnType: number): string => {
  switch (cruxDefnType) {
    case crux_defn_type_e.SEGMENT_DURATION_ACTIVE:
      return t`ELEMNT devices will display a graph of Strava Live Segments you have starred and synced to Wahoo.`;
    case crux_defn_type_e.CLIMB_DURATION_ACTIVE:
      return t`Supported ELEMNT devices will display an elevation graph and elapsed Summit Live Time.`;
    case crux_defn_type_e.WIND_DIAL:
      return t`ACE devices will display a wind speed dial and resistance ratio chart.`;
    default:
      return "";
  }
};

const getCruxPermanentFieldTitle = (cruxDefnType: number): string => {
  switch (cruxDefnType) {
    case crux_defn_type_e.SEGMENT_DURATION_ACTIVE:
      return t`Strava Live Segments`;
    case crux_defn_type_e.CLIMB_DURATION_ACTIVE:
      return t`Summit Segments`;
    case crux_defn_type_e.WIND_DIAL:
      return t`Wind metrics`;
    default:
      return "";
  }
};

interface StaticWorkoutFieldsProps {
  isMapPage: boolean;
  showElevationProfile: boolean;
  isPedalMonitorPage?: boolean;
  toggleShowElevationProfile: () => void;
  cruxPermanentDisplayFields: BoltDisplayField[];
  pedalMonitorGraphType: number | undefined;
  openCategoryDialog: (fieldToReplace?: BoltDisplayField, pedalMonitorGraphType?: boolean) => void;
  isPlannedWorkoutPage: boolean;
  workoutProfile?: WSMWorkoutProfile;
}
function StaticWorkoutFields({
  isMapPage,
  showElevationProfile,
  isPedalMonitorPage,
  toggleShowElevationProfile,
  cruxPermanentDisplayFields,
  pedalMonitorGraphType,
  openCategoryDialog,
  isPlannedWorkoutPage,
  workoutProfile,
}: StaticWorkoutFieldsProps) {
  const { workoutFieldTranslations, pedalMonitorGraphTypeFieldTranslations } =
    useWorkoutPageFieldStrings();

  const shouldShowElevationProfileOption = useMemo(() => {
    return isMapPage && workoutProfile?.workout_type_family_id === WahooWorkoutTypeFamily.Cycling;
  }, [isMapPage, workoutProfile?.workout_type_family_id]);

  const staticDisplayFieldListItems = useMemo(() => {
    return [
      {
        hidden: !isMapPage,
        id: "mapStaticField",
        variant: MenuListItemVariant.NoAction,
        content: (
          <Stack direction="row" gap={2} alignItems="center">
            <Box>
              <FancyIcon size="small" color="info" icon={<WfIconMiscMapTrifoldRegular />} />
            </Box>
            <Stack>
              <Typography variant="prose-base-medium" color="text.primary">
                <Trans>Map</Trans>
              </Typography>
              <Typography variant="prose-sm" color="text.secondary">
                <Trans>Displays your location, route navigation, and workout breadcrumbs.</Trans>
              </Typography>
            </Stack>
          </Stack>
        ),
        sx: {
          "&.MuiListItem-root": {
            borderRadius: 0,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          },
        },
      },
      {
        hidden: !shouldShowElevationProfileOption,
        id: "showElevationProfile",
        variant: MenuListItemVariant.NoAction,
        content: t`Show elevation profile on ELEMNT`,
        sx: {
          "&.MuiListItem-root.MuiPaper-rounded": {
            borderRadius: 0,
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
          },
        },
        actionComponent: (
          <Switch
            aria-label="show elevation profile"
            color="info"
            checked={showElevationProfile}
            onClick={toggleShowElevationProfile}
          />
        ),
      },
      {
        hidden: !isPedalMonitorPage,
        id: `pedal-static`,
        variant: MenuListItemVariant.NoAction,
        content: (
          <Stack direction="row" gap={2} alignItems="center">
            <Box>
              <FancyIcon size="small" color="info" icon={<WfIconSportBoltRegular />} />
            </Box>
            <Stack>
              <Typography variant="prose-base-medium" color="text.primary">
                <Trans>Pedaling efficiency graph</Trans>
              </Typography>
              <Typography variant="prose-sm" color="text.secondary">
                <Trans>
                  ELEMNT devices will display a graph of your left/right pedaling efficiency when
                  connected to a compatible sensor.
                </Trans>
              </Typography>
            </Stack>
          </Stack>
        ),
        sx: {
          "&.MuiListItem-root.MuiPaper-rounded": {
            borderRadius: 0,
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
          },
        },
      },
      {
        hidden: !isPedalMonitorPage,
        id: "pedal-action",
        variant: MenuListItemVariant.Action,
        content: pedalMonitorGraphTypeFieldTranslations[pedalMonitorGraphType || 0],
        sx: {
          "&.MuiListItem-root.MuiPaper-rounded": {
            borderRadius: 0,
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
          },
        },
        action: () => openCategoryDialog(undefined, true),
        actionComponent: <WfIconNavigationChevronRightRegular />,
      },
      {
        hidden: !isPlannedWorkoutPage,
        id: `planned-workout-static`,
        variant: MenuListItemVariant.NoAction,
        content: (
          <Stack direction="row" gap={2} alignItems="center">
            <Box>
              <FancyIcon size="small" color="info" icon={<WfIconMiscChartBarRegular />} />
            </Box>
            <Stack>
              <Typography variant="prose-base-medium" color="text.primary">
                <Trans>Planned workout graph</Trans>
              </Typography>
              <Typography variant="prose-sm" color="text.secondary">
                <Trans>
                  Displays your planned workout graph so you can easily follow your plan throughout
                  your workout.
                </Trans>
              </Typography>
            </Stack>
          </Stack>
        ),
        sx: {
          "&.MuiListItem-root.MuiPaper-rounded": {
            borderRadius: 0,
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
          },
        },
      },
      ...cruxPermanentDisplayFields.map((field, index) => {
        const fieldName = workoutFieldTranslations[field.getId()];
        const isFirstField = index === 0;
        const isLastField = index === cruxPermanentDisplayFields.length - 1;
        const icon = cruxPermanentFieldsIconMap[field?.getId() ?? -1] ?? null;
        const description = getCruxPermanentFieldDescription(field?.getId() ?? -1);
        const title = getCruxPermanentFieldTitle(field?.getId() ?? -1);

        return {
          hidden: cruxPermanentDisplayFields.length === 0,
          id: `static-display-field-${fieldName}`,
          variant: MenuListItemVariant.NoAction,
          content: (
            <Stack direction="row" gap={2} alignItems="center">
              {icon && (
                <Box>
                  <FancyIcon size="small" color="info" icon={icon} />
                </Box>
              )}
              <Stack>
                <Typography variant="prose-base-medium" color="text.primary">
                  {title || fieldName}
                </Typography>
                {description && (
                  <Typography variant="prose-sm" color="text.secondary">
                    {description}
                  </Typography>
                )}
              </Stack>
            </Stack>
          ),
          sx: {
            "&.MuiListItem-root": {
              borderRadius: 0,
              borderTopLeftRadius: isFirstField ? 8 : 0,
              borderTopRightRadius: isFirstField ? 8 : 0,
              borderBottomLeftRadius: isLastField ? 8 : 0,
              borderBottomRightRadius: isLastField ? 8 : 0,
            },
          },
        };
      }),
    ];
  }, [
    isMapPage,
    shouldShowElevationProfileOption,
    showElevationProfile,
    toggleShowElevationProfile,
    isPedalMonitorPage,
    pedalMonitorGraphTypeFieldTranslations,
    pedalMonitorGraphType,
    isPlannedWorkoutPage,
    cruxPermanentDisplayFields,
    openCategoryDialog,
    workoutFieldTranslations,
  ]);
  return (
    <MenuList
      listItems={staticDisplayFieldListItems}
      disableGutters
      columnCount={1}
      variant="outlined"
    />
  );
}

export default StaticWorkoutFields;
